import type {CmLogger} from "@cm/log"
import {
    GetMaterialArticleIdGQL,
    GetMaterialIdByArticleIdGQL,
    GetMaterialIdGQL,
    GetMaterialLegacyIdGQL,
    GetMaterialSizeGQL,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/bd-cloud.generated"
import {
    Color,
    ColorWithSubstrate,
    Material,
    MaterialTransform,
    QuiltedMaterial,
    MaterialOrColor,
    isMaterial,
} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {fetchThrowingErrors} from "@app/common/helpers/api/fetch"
import {
    FindMaterial,
    ImageRgba,
    isTemplateNode,
    MaterialLikeNode,
    MaterialOutput,
    MaterialReference,
    OverlayMaterialColor,
    Parameters,
    TemplateGraph,
    TemplateInstance,
    TemplateNode,
    TemplateReference,
    isMaterialLikeNode,
} from "@cm/template-nodes"
import {deserializeGraphNode, serializeGraphNode} from "@cm/graph-v3"
import {Matrix4} from "@cm/math"
import {uuid4} from "@cm/utils"
import {linearColorToSrgbHex, srgbHexToLinearColor} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/color"

import {BdIoIds} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/constants"
import {environment} from "@app/common/components/viewers/configurator/extensions/bd-cloud/environment/environment"
import {getImageOrigin, getMaterialOrigin, getParameterStorage} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/parameter-storage"
import {getRelatedInputs} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/handles"
import {isQuiltSetupInstance, removeUnreferencedQuiltSetups} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/quilt-setup"

const TEMPLATE_NAMESPACE = "template"

// region --- Helper extractors (private) ---

function getColor(node: MaterialLikeNode | null): string | undefined {
    if (node instanceof OverlayMaterialColor) {
        const overlay = node.parameters.overlay
        if (!(overlay instanceof ImageRgba)) return undefined

        const color = overlay.parameters.color
        if (Array.isArray(color)) return linearColorToSrgbHex(color)
    }
    return undefined
}

function getColorSubstrateId(node: OverlayMaterialColor): number | undefined {
    const substrate = node.parameters.material
    if (substrate instanceof MaterialReference) return substrate.parameters.materialId
    return undefined
}

// endregion

// region --- ID resolution helpers ---

export async function getMaterialArticleId(node: TemplateNode | null, context: ExtensionContext, logger?: CmLogger): Promise<string | undefined> {
    if (node === null) return undefined

    if (node instanceof FindMaterial && typeof node.parameters.articleId === "string") return node.parameters.articleId

    if (node instanceof MaterialReference) {
        const query = context.injector.get(GetMaterialArticleIdGQL)
        const result = await fetchThrowingErrors(query)({materialLegacyId: node.parameters.materialId})
        const id = result?.configuratorMaterial?.articleId
        if (id) return id
        logger?.warn(`Could not resolve material article ID for node ${node}`)
    }

    return undefined
}

export async function resolveMaterialNodeToId(node: TemplateNode | null, context: ExtensionContext): Promise<string | undefined> {
    if (node === null) return undefined

    if (node instanceof FindMaterial && typeof node.parameters.articleId === "string") {
        const organizationId = context.getOrganizationId()
        if (!organizationId) throw new Error("Organization ID is not available")
        const query = context.injector.get(GetMaterialIdByArticleIdGQL)
        const result = await fetchThrowingErrors(query)({articleId: node.parameters.articleId, organizationId})
        return result?.configuratorMaterial?.id
    }

    if (node instanceof MaterialReference) {
        const query = context.injector.get(GetMaterialIdGQL)
        const result = await fetchThrowingErrors(query)({materialLegacyId: node.parameters.materialId})
        return result?.configuratorMaterial?.id
    }

    return undefined
}

export async function getMaterialLegacyId(materialId: string | undefined, context: ExtensionContext): Promise<number | undefined> {
    if (!materialId) return undefined
    const query = context.injector.get(GetMaterialLegacyIdGQL)
    const result = await fetchThrowingErrors(query)({materialId})
    return result.configuratorMaterial?.legacyId
}

export async function getMaterialLegacyIdByArticleId(articleId: string, context: ExtensionContext): Promise<number> {
    const organizationId = context.getOrganizationId()
    if (!organizationId) throw new Error("Organization ID is not available")
    const query = context.injector.get(GetMaterialIdByArticleIdGQL)
    const result = await fetchThrowingErrors(query)({articleId, organizationId})
    if (!result.configuratorMaterial) throw new Error(`Could not find material with articleId: ${articleId}`)
    return result.configuratorMaterial.legacyId
}

export async function resolveMaterialNodeToLegacyId(node: TemplateNode, context: ExtensionContext): Promise<number | undefined> {
    if (node instanceof MaterialReference) return node.parameters.materialId
    if (node instanceof FindMaterial && typeof node.parameters.articleId === "string") return getMaterialLegacyIdByArticleId(node.parameters.articleId, context)

    if (isQuilt(node)) {
        const params = getQuiltSetupTemplateParams(node)
        const baseFabric = params.parameters[BdIoIds.baseFabricInputId]
        if (!isTemplateNode(baseFabric) || !isMaterialLikeNode(baseFabric))
            throw new Error("Cannot resolve material legacy ID: quilt base fabric is not a material")
        return resolveMaterialNodeToLegacyId(baseFabric, context)
    }

    throw new Error(`Cannot resolve material legacy ID: unsupported node type ${node.constructor.name}`)
}

// endregion

// region --- Material dimensions ---

export type MaterialDimensions = {
    width: number
    height: number
}

export async function getMaterialDimensions(materialId: string, context: ExtensionContext): Promise<MaterialDimensions> {
    const query = context.injector.get(GetMaterialSizeGQL)
    const result = await fetchThrowingErrors(query)({id: materialId})

    const dimensions = result.configuratorMaterial?.textureGroupDimensions
    if (!dimensions || dimensions.width == null || dimensions.height == null) throw new Error("Material does not have texture group dimensions")

    return {width: dimensions.width, height: dimensions.height}
}

// endregion

// region --- Quilt helpers ---

export function isQuilt(material: TemplateNode | undefined | null): material is MaterialOutput {
    return material instanceof MaterialOutput && material.parameters.outputId === BdIoIds.quiltOutputId && isQuiltSetupInstance(material.parameters.template)
}

export function getQuiltSetupTemplateParams(materialOutput: MaterialOutput): Parameters {
    const quiltSetupTemplate = materialOutput.parameters.template
    if (!(quiltSetupTemplate instanceof TemplateInstance)) throw new Error("Quilt setup template is not a template instance")
    return quiltSetupTemplate.parameters.parameters
}

/* The bd api only ever writes to the parameters of the root instance. A quilt that is not set there lives in a template further down the tree
(e.g. a master that is instantiated here) and cannot be modified from here, so a copy of it is put on the root and returned instead. */
function getQuiltOnRoot(inputId: string, quilt: MaterialOutput, setOnRoot: boolean, templateGraph: TemplateGraph): MaterialOutput {
    if (setOnRoot) return quilt

    const quiltSetupTemplate = deserializeGraphNode(serializeGraphNode(quilt.parameters.template), TEMPLATE_NAMESPACE)
    if (!(quiltSetupTemplate instanceof TemplateInstance)) throw new Error("Quilt setup template is not a template instance")
    quiltSetupTemplate.updateParameters({id: uuid4()})
    templateGraph.parameters.nodes.addEntry(quiltSetupTemplate)

    const quiltOnRoot = new MaterialOutput({parameters: {template: quiltSetupTemplate, outputId: BdIoIds.quiltOutputId, name: quilt.parameters.name}})
    const {storingInstance, key} = getParameterStorage(templateGraph, inputId)
    storingInstance.parameters.parameters.updateParameters({[key]: quiltOnRoot})
    return quiltOnRoot
}

function modifyQuiltParams(
    inputId: string,
    quilt: MaterialOutput,
    setOnRoot: boolean,
    context: ExtensionContext,
    modifier: (params: Parameters) => void,
): void {
    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        modifier(getQuiltSetupTemplateParams(getQuiltOnRoot(inputId, quilt, setOnRoot, templateGraph)))
    })
}

/* The fabric transform is stored next to the slot for a plain fabric and on the quilt setup for a quilted one,
as the one next to the slot applies to everything in the slot and would move the quilt pattern along. */
const fabricTransformQuiltParamIds = {
    horizontalOffset: BdIoIds.quiltedFabricFabricHOffsetInputId,
    verticalOffset: BdIoIds.quiltedFabricFabricVOffsetInputId,
    rotation: BdIoIds.quiltedFabricFabricRotationInputId,
}

type FabricTransformParam = keyof typeof fabricTransformQuiltParamIds

function slotParamId(slotKey: string, param: FabricTransformParam): string {
    return `${slotKey}-${param}`
}

export function getSlotFabricTransform(slotParams: Parameters, slotKey: string): MaterialTransform {
    const getValue = (param: FabricTransformParam) => (slotParams.parameters[slotParamId(slotKey, param)] as number | undefined) ?? 0
    return {horizontalOffset: getValue("horizontalOffset"), verticalOffset: getValue("verticalOffset"), rotation: getValue("rotation")}
}

/* Hands the fabric transform over when a quilt is added to or removed from a slot. Nothing is left behind where it is taken from,
and a value that is not set there is not kept at the destination either. */
function moveFabricTransform(slotParams: Parameters, slotKey: string, quiltParams: Parameters, to: "quilt" | "slot"): void {
    const slotValues = {...slotParams.parameters}
    const quiltValues = {...quiltParams.parameters}

    for (const param of Object.keys(fabricTransformQuiltParamIds) as FabricTransformParam[]) {
        const slotId = slotParamId(slotKey, param)
        const quiltId = fabricTransformQuiltParamIds[param]
        const [source, sourceId, target, targetId] =
            to === "quilt" ? ([slotValues, slotId, quiltValues, quiltId] as const) : ([quiltValues, quiltId, slotValues, slotId] as const)

        if (source[sourceId] === undefined) delete target[targetId]
        else target[targetId] = source[sourceId]
        delete source[sourceId]
    }

    slotParams.replaceParameters(slotValues)
    quiltParams.replaceParameters(quiltValues)
}

type QuiltData = {
    materialId: string | undefined
    materialArticleId: string | undefined
    quiltId: string
    quiltStrength: number
    width: number
    height: number
    quiltHorizontalOffset: number
    quiltVerticalOffset: number
    fabricHorizontalOffset: number
    fabricVerticalOffset: number
    fabricRotation: number
}

export async function getQuiltData(material: TemplateNode | undefined | null, context: ExtensionContext): Promise<QuiltData | undefined> {
    if (!isQuilt(material)) return undefined

    const quiltSetupTemplate = material.parameters.template

    if (!(quiltSetupTemplate instanceof TemplateInstance)) return undefined

    const instanceParameters = quiltSetupTemplate.parameters.parameters

    const baseFabric = instanceParameters.parameters[BdIoIds.baseFabricInputId] //FindMaterial or MaterialReference
    if (!isTemplateNode(baseFabric)) throw new Error("Base fabric is not a template node")
    const [materialArticleId, materialId] = await Promise.all([
        getMaterialArticleId(baseFabric, context),
        isMaterialLikeNode(baseFabric) ? resolveMaterialNodeToId(baseFabric, context) : Promise.resolve(undefined),
    ])
    if (!materialArticleId && !materialId) throw new Error("Could not resolve material ID for quilt base fabric")

    const fabricHorizontalOffset = (instanceParameters.parameters[fabricTransformQuiltParamIds.horizontalOffset] as number | undefined) ?? 0.0
    const fabricVerticalOffset = (instanceParameters.parameters[fabricTransformQuiltParamIds.verticalOffset] as number | undefined) ?? 0.0
    const fabricRotation = (instanceParameters.parameters[fabricTransformQuiltParamIds.rotation] as number | undefined) ?? 0.0

    const quiltPattern = instanceParameters.parameters[BdIoIds.quiltPatternInputId] //MaterialReference
    if (!(quiltPattern instanceof MaterialReference)) throw new Error("Quilt pattern is not a template node")

    const getMaterialIdQuery = context.injector.get(GetMaterialIdGQL)
    const quiltIdResult = await fetchThrowingErrors(getMaterialIdQuery)({materialLegacyId: quiltPattern.parameters.materialId})
    if (!quiltIdResult.configuratorMaterial) throw new Error("Could not resolve quilt material ID")

    const quiltStrength = (instanceParameters.parameters[BdIoIds.quiltStrengthInputId] as number | undefined) ?? 0.2
    const quiltHorizontalOffset = (instanceParameters.parameters[BdIoIds.quiltedFabricQuiltHOffsetInputId] as number | undefined) ?? 0.0
    const quiltVerticalOffset = (instanceParameters.parameters[BdIoIds.quiltedFabricQuiltVOffsetInputId] as number | undefined) ?? 0.0

    const quiltDimensions = await getMaterialDimensions(quiltIdResult.configuratorMaterial.id, context)

    return {
        materialId,
        materialArticleId,
        quiltId: quiltIdResult.configuratorMaterial.id,
        quiltStrength,
        quiltHorizontalOffset,
        quiltVerticalOffset,
        fabricHorizontalOffset,
        fabricVerticalOffset,
        fabricRotation,
        ...quiltDimensions,
    }
}

// endregion

// region --- Entry point ---

/**
 * Resolves a single TemplateNode to its MaterialOrColor representation.
 *
 * Returns undefined if the node cannot be narrowed to a recognized material type.
 * The transform of a plain material is stored next to its slot, not on the node: it is 0 here and added by resolveSlotMaterialOrColor.
 */
async function resolveMaterialOrColor(node: TemplateNode, context: ExtensionContext): Promise<MaterialOrColor | undefined> {
    if (node instanceof OverlayMaterialColor) {
        return {
            color: getColor(node),
            colorSubstrateLegacyId: getColorSubstrateId(node),
        } satisfies ColorWithSubstrate
    }

    if (isQuilt(node)) {
        const quiltData = await getQuiltData(node, context)
        if (!quiltData) return undefined
        return {
            materialId: quiltData.materialId,
            materialArticleId: quiltData.materialArticleId,
            quiltId: quiltData.quiltId,
            quiltStrength: quiltData.quiltStrength,
            horizontalOffset: quiltData.fabricHorizontalOffset,
            verticalOffset: quiltData.fabricVerticalOffset,
            rotation: quiltData.fabricRotation,
            quiltHorizontalOffset: quiltData.quiltHorizontalOffset,
            quiltVerticalOffset: quiltData.quiltVerticalOffset,
        } satisfies QuiltedMaterial
    }

    if (node instanceof ImageRgba) {
        const color = node.parameters.color
        return {
            color: Array.isArray(color) ? linearColorToSrgbHex(color) : undefined,
        } satisfies Color
    }

    if (isMaterialLikeNode(node)) {
        const [materialId, materialArticleId] = await Promise.all([resolveMaterialNodeToId(node, context), getMaterialArticleId(node, context)])
        return {
            materialId,
            materialArticleId,
            horizontalOffset: 0,
            verticalOffset: 0,
            rotation: 0,
        } satisfies Material
    }

    return undefined
}

/** Resolves the node held by the slot inputId, including the transform of a plain material (see getSlotFabricTransform). */
export async function resolveSlotMaterialOrColor(inputId: string, node: TemplateNode, context: ExtensionContext): Promise<MaterialOrColor | undefined> {
    const value = await resolveMaterialOrColor(node, context)
    if (!value || !isMaterial(value)) return value

    const {storingInstance, key} = getParameterStorage(context.sceneManagerService.$templateGraph(), inputId)
    return {...value, ...getSlotFabricTransform(storingInstance.parameters.parameters, key)}
}

// endregion

// region --- Material setters ---

function applyMaterialToInput(inputId: string, legacyId: number, value: MaterialLikeNode | undefined, setOnRoot: boolean, templateGraph: TemplateGraph): void {
    const newMaterialRef = () => new MaterialReference({parameters: {materialId: legacyId, name: "Material Reference", materialRevisionId: null}})

    // Quilt: write to the base fabric input on the quilt's setup template
    if (value && isQuilt(value)) {
        const params = getQuiltSetupTemplateParams(getQuiltOnRoot(inputId, value, setOnRoot, templateGraph))
        const fabricKey = BdIoIds.baseFabricInputId
        const currentFabric = params.parameters[fabricKey]

        if (currentFabric instanceof MaterialReference) currentFabric.updateParameters({materialId: legacyId, materialRevisionId: null})
        else params.updateParameters({[fabricKey]: newMaterialRef()})

        return
    }

    // Non-quilt root: only supported when the existing value is itself a MaterialReference
    if (setOnRoot && value instanceof MaterialReference) {
        value.updateParameters({materialId: legacyId, materialRevisionId: null})
        return
    }

    // Non-quilt, non-root, non-materialreference: write new material ref into the resolved parameter storage
    const {storingInstance, key} = getParameterStorage(templateGraph, inputId)
    storingInstance.parameters.parameters.updateParameters({[key]: newMaterialRef()})
}

export async function setMaterial(inputId: string, materialArticleId: string, context: ExtensionContext): Promise<void> {
    const legacyId = await getMaterialLegacyIdByArticleId(materialArticleId, context)
    const {value, setOnRoot} = getMaterialOrigin(inputId, context)

    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        applyMaterialToInput(inputId, legacyId, value, setOnRoot, templateGraph)
    })
}

export async function setHandleMaterial(inputId: string, materialArticleId: string, context: ExtensionContext): Promise<void> {
    const legacyId = await getMaterialLegacyIdByArticleId(materialArticleId, context)
    const descriptors = context.sceneManagerService.$descriptorList()
    const relatedInputs = getRelatedInputs(inputId, descriptors)

    const origins = relatedInputs.map((id) => ({id, ...getMaterialOrigin(id, context)}))

    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        for (const {id, value, setOnRoot} of origins) {
            applyMaterialToInput(id, legacyId, value, setOnRoot, templateGraph)
        }
    })
}

function deleteQuilt(inputId: string, currentValue: MaterialOutput, setOnRoot: boolean, context: ExtensionContext): void {
    context.sceneManagerService.modifyTemplateGraph((templateGraph: TemplateGraph) => {
        //The base fabric is taken from the quilt on the root, the plain fabric then overrides a quilt from further down the tree
        const instanceParameters = getQuiltSetupTemplateParams(getQuiltOnRoot(inputId, currentValue, setOnRoot, templateGraph))
        const baseFabric = instanceParameters.parameters[BdIoIds.baseFabricInputId]
        if (!isTemplateNode(baseFabric) || !isMaterialLikeNode(baseFabric)) throw new Error("Cannot delete quilt: base fabric is not a material")

        const {storingInstance, key} = getParameterStorage(templateGraph, inputId)
        const slotParams = storingInstance.parameters.parameters
        moveFabricTransform(slotParams, key, instanceParameters, "slot")
        slotParams.updateParameters({[key]: baseFabric})
        removeUnreferencedQuiltSetups(templateGraph)
    })
}

function updateQuiltPattern(
    inputId: string,
    currentValue: MaterialOutput,
    setOnRoot: boolean,
    quiltLegacyId: number,
    quiltDimensions: MaterialDimensions,
    context: ExtensionContext,
): void {
    modifyQuiltParams(inputId, currentValue, setOnRoot, context, (params) => {
        params.updateParameters({
            [BdIoIds.quiltWidthInputId]: quiltDimensions.width,
            [BdIoIds.quiltHeightInputId]: quiltDimensions.height,
        })

        const material = params.parameters[BdIoIds.quiltPatternInputId]
        if (material && material instanceof MaterialReference) material.updateParameters({materialId: quiltLegacyId, materialRevisionId: null})
        else
            params.updateParameters({
                [BdIoIds.quiltPatternInputId]: new MaterialReference({
                    parameters: {materialId: quiltLegacyId, name: "Material Reference", materialRevisionId: null},
                }),
            })
    })
}

function initNewQuilt(
    inputId: string,
    baseFabricLegacyId: number,
    quiltLegacyId: number,
    quiltDimensions: MaterialDimensions,
    context: ExtensionContext,
): void {
    context.sceneManagerService.modifyTemplateGraph((templateGraph: TemplateGraph) => {
        const baseFabric = new MaterialReference({parameters: {materialId: baseFabricLegacyId, name: "Material Reference", materialRevisionId: null}})
        const quiltPattern = new MaterialReference({parameters: {materialId: quiltLegacyId, name: "Material Reference", materialRevisionId: null}})

        const templateInstance = new TemplateInstance({
            parameters: {
                id: uuid4(),
                name: "Setup Quilt",
                lockedTransform: Matrix4.identity().toArray(),
                visible: true,
                template: new TemplateReference({
                    parameters: {
                        templateId: environment.quiltSetupTemplateId,
                        templateRevisionId: null,
                    },
                }),
                parameters: new Parameters({
                    parameters: {
                        [BdIoIds.baseFabricInputId]: baseFabric,
                        [BdIoIds.quiltPatternInputId]: quiltPattern,
                        [BdIoIds.quiltStrengthInputId]: 0.2,
                        [BdIoIds.quiltWidthInputId]: quiltDimensions.width,
                        [BdIoIds.quiltHeightInputId]: quiltDimensions.height,
                    },
                }),
            },
        })

        templateGraph.parameters.nodes.addEntry(templateInstance)

        const materialOutput = new MaterialOutput({parameters: {template: templateInstance, outputId: BdIoIds.quiltOutputId, name: "aaa"}})
        const {storingInstance, key} = getParameterStorage(templateGraph, inputId)
        const slotParams = storingInstance.parameters.parameters
        moveFabricTransform(slotParams, key, templateInstance.parameters.parameters, "quilt")
        slotParams.updateParameters({[key]: materialOutput})
    })
}

export async function setQuilt(inputId: string, quiltId: string | undefined, context: ExtensionContext): Promise<void> {
    if (quiltId === undefined) {
        const {value, setOnRoot} = getMaterialOrigin(inputId, context)
        if (!value || !isQuilt(value)) throw new Error("Cannot delete quilt")
        deleteQuilt(inputId, value, setOnRoot, context)
        return
    }

    const quiltLegacyId = await getMaterialLegacyId(quiltId, context)
    if (!quiltLegacyId) throw new Error("Could not resolve quilt legacy ID")
    const quiltDimensions = await getMaterialDimensions(quiltId, context)

    //The slot is only read now: another call may have modified it during the lookups above, e.g. by copying its quilt to the root
    const {value, setOnRoot} = getMaterialOrigin(inputId, context)
    if (value && isQuilt(value)) {
        updateQuiltPattern(inputId, value, setOnRoot, quiltLegacyId, quiltDimensions, context)
        return
    }

    if (!value) throw new Error("Cannot add quilt: no base fabric found")
    const baseFabricLegacyId = await resolveMaterialNodeToLegacyId(value, context)
    if (!baseFabricLegacyId) throw new Error("Cannot add quilt: could not retrieve material id")
    //Same here, a quilt added meanwhile would be overwritten and its setup left behind: start over with what the slot holds now
    if (getMaterialOrigin(inputId, context).value !== value) return setQuilt(inputId, quiltId, context)
    initNewQuilt(inputId, baseFabricLegacyId, quiltLegacyId, quiltDimensions, context)
}

export function setQuiltStrength(inputId: string, strength: number, context: ExtensionContext): void {
    const {value, setOnRoot} = getMaterialOrigin(inputId, context)
    if (!value || !isQuilt(value)) throw new Error("Cannot set quilt strength: input does not contain a quilt")

    modifyQuiltParams(inputId, value, setOnRoot, context, (params) => params.updateParameters({[BdIoIds.quiltStrengthInputId]: strength}))
}

function setMaterialTransformParam(inputId: string, param: FabricTransformParam, value: number, context: ExtensionContext): void {
    const {value: currentValue, setOnRoot} = getMaterialOrigin(inputId, context)
    if (currentValue && isQuilt(currentValue)) {
        modifyQuiltParams(inputId, currentValue, setOnRoot, context, (params) => params.updateParameters({[fabricTransformQuiltParamIds[param]]: value}))
    } else {
        context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key} = getParameterStorage(templateGraph, inputId)
            storingInstance.parameters.parameters.updateParameters({[slotParamId(key, param)]: value})
        })
    }
}

export function setMaterialHorizontalOffset(inputId: string, offset: number, context: ExtensionContext): void {
    setMaterialTransformParam(inputId, "horizontalOffset", offset, context)
}

export function setMaterialVerticalOffset(inputId: string, offset: number, context: ExtensionContext): void {
    setMaterialTransformParam(inputId, "verticalOffset", offset, context)
}

export function setMaterialRotation(inputId: string, rotation: number, context: ExtensionContext): void {
    setMaterialTransformParam(inputId, "rotation", rotation, context)
}

//Like the material, the transform is applied to all handles. Handles do not allow quilts, so it is always stored next to the slot.
function setHandleMaterialTransformParam(inputId: string, param: FabricTransformParam, value: number, context: ExtensionContext): void {
    const relatedInputs = getRelatedInputs(inputId, context.sceneManagerService.$descriptorList())

    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        for (const id of relatedInputs) {
            const {storingInstance, key} = getParameterStorage(templateGraph, id)
            storingInstance.parameters.parameters.updateParameters({[slotParamId(key, param)]: value})
        }
    })
}

export function setHandleMaterialHorizontalOffset(inputId: string, offset: number, context: ExtensionContext): void {
    setHandleMaterialTransformParam(inputId, "horizontalOffset", offset, context)
}

export function setHandleMaterialVerticalOffset(inputId: string, offset: number, context: ExtensionContext): void {
    setHandleMaterialTransformParam(inputId, "verticalOffset", offset, context)
}

export function setHandleMaterialRotation(inputId: string, rotation: number, context: ExtensionContext): void {
    setHandleMaterialTransformParam(inputId, "rotation", rotation, context)
}

export function setQuiltHorizontalOffset(inputId: string, offset: number, context: ExtensionContext): void {
    const {value: currentValue, setOnRoot} = getMaterialOrigin(inputId, context)
    if (!currentValue || !isQuilt(currentValue)) throw new Error("Cannot set quilt offset: input does not contain a quilt")
    modifyQuiltParams(inputId, currentValue, setOnRoot, context, (params) => params.updateParameters({[BdIoIds.quiltedFabricQuiltHOffsetInputId]: offset}))
}

export function setQuiltVerticalOffset(inputId: string, offset: number, context: ExtensionContext): void {
    const {value: currentValue, setOnRoot} = getMaterialOrigin(inputId, context)
    if (!currentValue || !isQuilt(currentValue)) throw new Error("Cannot set quilt offset: input does not contain a quilt")
    modifyQuiltParams(inputId, currentValue, setOnRoot, context, (params) => params.updateParameters({[BdIoIds.quiltedFabricQuiltVOffsetInputId]: offset}))
}

export function setColor(inputId: string, color: string, context: ExtensionContext): void {
    const {value, setOnRoot} = getImageOrigin(inputId, context)

    if (value instanceof ImageRgba && setOnRoot) {
        context.sceneManagerService.modifyTemplateGraph(() => {
            value.updateParameters({color: srgbHexToLinearColor(color)})
        })
        return
    }

    if (!value || (value instanceof ImageRgba && !setOnRoot)) {
        context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key} = getParameterStorage(templateGraph, inputId)
            storingInstance.parameters.parameters.updateParameters({
                [key]: new ImageRgba({parameters: {color: srgbHexToLinearColor(color), name: "color", alpha: 1}}),
            })
        })
        return
    }

    throw new Error("Cannot set color: unsupported node type, expected ImageRgba")
}

function applyColorWithSubstrateToInput(
    inputId: string,
    color: string,
    substrateId: number,
    value: MaterialLikeNode | undefined,
    setOnRoot: boolean,
    templateGraph: TemplateGraph,
): void {
    if (value instanceof OverlayMaterialColor && setOnRoot) {
        const overlay = value.parameters.overlay
        const substrate = value.parameters.material
        if (overlay instanceof ImageRgba && substrate instanceof MaterialReference) {
            overlay.updateParameters({color: srgbHexToLinearColor(color)})
            substrate.updateParameters({materialId: substrateId, materialRevisionId: null})
            return
        }
    }

    if (value && isQuilt(value)) throw new Error("Cannot set color with substrate: input holds a quilt") //Overwriting the slot would leave it orphaned

    const imageRgba = new ImageRgba({parameters: {color: srgbHexToLinearColor(color), name: "color", alpha: 1}})
    const substrate = new MaterialReference({parameters: {materialId: substrateId, name: "Material Reference", materialRevisionId: null}})
    const overlay = new OverlayMaterialColor({parameters: {material: substrate, overlay: imageRgba, name: "Overlay Color", size: [10, 10]}})
    const {storingInstance, key} = getParameterStorage(templateGraph, inputId)
    storingInstance.parameters.parameters.updateParameters({[key]: overlay})
}

export function setColorWithSubstrate(inputId: string, color: string, substrateId: number, context: ExtensionContext): void {
    const {value, setOnRoot} = getMaterialOrigin(inputId, context)
    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        applyColorWithSubstrateToInput(inputId, color, substrateId, value, setOnRoot, templateGraph)
    })
}

export function setHandleColorWithSubstrate(inputId: string, color: string, substrateId: number, context: ExtensionContext): void {
    const descriptors = context.sceneManagerService.$descriptorList()
    const relatedInputs = getRelatedInputs(inputId, descriptors)

    const origins = relatedInputs.map((id) => ({id, ...getMaterialOrigin(id, context)}))

    context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
        for (const {id, value, setOnRoot} of origins) {
            applyColorWithSubstrateToInput(id, color, substrateId, value, setOnRoot, templateGraph)
        }
    })
}

// endregion
